import { createApi } from '@reduxjs/toolkit/query/react'
import TableWebAPI from '@api/TableWebAPI'
import jsonApiNormalize from '@base/json-api-normalizer'
import { receiveEntities } from '@actions/entity_actions'
import { toastSuccess } from '@actions/toast_actions'
import { serializableError } from '@utils/serializable_error'
import axiosBaseQuery from '@base/axios-base-query'

const loadTableById = async (tableId: string) => {
  const include = ['photo', 'fields', 'fields.select_options', 'rows', 'rows.photos']
  // Propagate any errors to the caller to be handled in queryFn
  const response = await TableWebAPI.loadTable(tableId, include.join(','))
  return { data: response.data as any }
}

export const viewsApi = createApi({
  reducerPath: 'viewsApi',
  tagTypes: ['View'],
  baseQuery: axiosBaseQuery({
    baseUrl: '/api/v2/',
  }),
  endpoints: builder => ({
    getViewByUuid: builder.query<{ view: any; table: any }, { viewUuid: string; tableId: string }>({
      providesTags: (_result, _err, { viewUuid }) => [{ type: 'View', uuid: viewUuid }],
      queryFn: async ({ viewUuid, tableId }, _queryApi, _extraOptions, baseQuery) => {
        try {
          // fetch the view using redux query
          const viewRequest = await baseQuery({
            url: `views/${viewUuid}`,
            method: 'get',
            requestId: `get-view-${viewUuid}`,
          })
          if ('error' in viewRequest && viewRequest.error) {
            // axiosBaseQuery already returns { status, data } error shape; just propagate it
            return { error: (viewRequest as any).error }
          }

          // fetch the table using TableWebApi
          const tableRequest = await loadTableById(tableId)

          // return the combined response
          return {
            data: {
              view: (viewRequest as any).data,
              table: tableRequest.data,
            },
          }
        } catch (error) {
          // Normalize thrown errors (likely AxiosError from TableWebAPI) to match axiosBaseQuery error shape
          const e: any = error
          const status = e?.response?.status ?? 'FETCH_ERROR'
          const data = e?.response?.data ?? e?.message ?? e
          return { error: { status, data } as any }
        }
      },
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          // onSuccess of both queries, perform some side effects
          const { data } = await queryFulfilled
          const normalizedTable = jsonApiNormalize(data.table, { camelizeKeys: false, camelizeTypeValues: false })
          const normalizedView = jsonApiNormalize(data.view, { camelizeKeys: false, camelizeTypeValues: false })
          const normalized = { ...normalizedTable, ...normalizedView }
          // dispatch the receiveEntities action to update normalized state
          dispatch(receiveEntities(normalized))
        } catch (err) {
          console.error(err)
        }
      },
    }),
    updateViewAccessLevel: builder.mutation({
      query: ({ name, externalAccessLevel, viewUuid }) => ({
        url: `views/${viewUuid}`,
        method: 'patch',
        data: {
          name,
          external_access_level: externalAccessLevel,
        },
        requestId: `update-view-${viewUuid}`,
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          // onSuccess of views query, perform some side effects
          const { data } = await queryFulfilled
          const normalized = jsonApiNormalize(data, { camelizeKeys: false, camelizeTypeValues: false })
          dispatch(receiveEntities(normalized))
          dispatch(toastSuccess('Share settings updated'))
        } catch (_) {
          return
        }
      },
      transformErrorResponse: (err: unknown) => {
        const e = err as any
        // Wrap baseQuery's { status, data } into an Axios-like error for serializableError; otherwise pass through
        return serializableError(
          e && typeof e === 'object' && 'status' in e
            ? ({
                isAxiosError: true,
                message: typeof e.data === 'string' ? e.data : undefined,
                response: { status: e.status, data: e.data },
              } as any)
            : e
        )
      },
    }),
  }),
})

export const { useGetViewByUuidQuery, useUpdateViewAccessLevelMutation } = viewsApi
